import { useState } from 'react';
import { AlertCircle, Mail, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export function EmailVerificationBanner() {
  const { user, refreshUser } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Don't show banner if email is verified or user is master
  if (!user || user.email_verified || user.is_master_user) {
    return null;
  }

  const handleResendEmail = async () => {
    setIsResending(true);
    setMessage(null);

    try {
      const response = await api.resendVerificationEmail({ email: user.email });
      setMessage({ type: 'success', text: response.message });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to resend verification email',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="bg-warning-50 border-l-4 border-warning-500 p-4 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-warning-600" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-warning-800">
            Email Verification Required
          </h3>
          <div className="mt-2 text-sm text-warning-700">
            <p>
              Please verify your email address (<strong>{user.email}</strong>) to start receiving
              newsletters. You can set up your preferences now, but newsletters will only be sent
              after verification.
            </p>
            <p className="mt-1">
              Check your inbox for the verification email. Don't forget to check your spam folder!
            </p>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleResendEmail}
              disabled={isResending}
              leftIcon={isResending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            >
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={refreshUser}
              leftIcon={<CheckCircle className="w-4 h-4" />}
            >
              I've Verified
            </Button>
          </div>
          {message && (
            <div
              className={`mt-3 text-sm ${
                message.type === 'success' ? 'text-success-700' : 'text-error-700'
              }`}
            >
              {message.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

